import { useLazyFetchJson } from 'common/utils/fetchJson';
import { useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, Alert, Spin, Typography, Space } from 'antd';
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export type TokenPayload = {
  token: string;
  exp: string;
  type: string;
  email: string;
};

const containsDigit = /\d/;
const notContainsSpecial = /^[a-zA-Z0-9]*$/;

type IProps = {
  payload: TokenPayload;
};

export const NewPassword = ({ payload }: IProps) => {
  const [form] = Form.useForm();
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validation, setValidation] = useState<boolean[]>([true, true, false, false, false]);

  const handleError = useCallback(
    (r: { status: string } | undefined) => {
      if (!r) return;
      let good = false;
      if (r.status === 'invalid') {
        setError('Invalid operation - missing params');
      } else if (r.status === 'fail') {
        setError('Invalid token');
      } else if (r.status === 'expired') {
        setError('This token has already expired. Please request a new one.');
      } else if (r.status === 'reused') {
        setError('This token has already been used. Please request a new one.');
      } else if (r.status === 'bad_user') {
        setError('Specified user no longer exists');
      } else if (r.status === 'bad_password') {
        setError('Password does not meet requirements');
      } else if (r.status === 'success') {
        good = true;
      }
      if (!good && !error) {
        // Set a generic error if none was set
        setError('An error occurred');
      }
    },
    [error],
  );
  const {
    fetch: submitVerify,
    isLoading: isVerifying,
    error: verifyError,
  } = useLazyFetchJson<{ status: string }>('/api/verifyToken', { method: 'POST' });
  const [init, setInit] = useState(false);

  useEffect(() => {
    submitVerify(undefined, {
      body: JSON.stringify(payload),
    }).then((result) => {
      if (result.ok) {
        handleError(result.data);
      }
    });
    setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { fetch: submitPassword, isLoading: isSubmitting } = useLazyFetchJson<{ status: string }>(
    '/api/resetPassword',
    { method: 'POST' },
  );

  const submitReset = useCallback(
    (values: { password: string }) => {
      submitPassword(undefined, {
        body: JSON.stringify(Object.assign({ password: values.password }, payload)),
      }).then((result) => {
        if (!result.ok) {
          return;
        }
        if (result.data.status === 'success') {
          setSuccess('Password successfully ' + (payload.type === 'reset' ? 'reset' : 'set') + '.');
        } else {
          handleError(result.data);
        }
      });
    },
    [handleError, payload, submitPassword],
  );

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const p = e.target.value;
    setPassword(p);
    setValidation([
      p !== payload.email,
      p === confirmPassword,
      p.length >= 8,
      containsDigit.test(p),
      !notContainsSpecial.test(p),
    ]);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    setValidation((v) => {
      const newV = [...v];
      newV[1] = password === e.target.value;
      return newV;
    });
  };

  const isFormValid = validation.every((valid) => valid);

  return (
    <>
      {success ? (
        <Alert
          message="Success!"
          description={
            <div>
              {success}
              <br />
              <br />
              Please{' '}
              <a href="/auth" style={{ color: '#2386b4', fontWeight: 'bold' }}>
                click here
              </a>{' '}
              to return back to the login page.
            </div>
          }
          type="success"
          showIcon
        />
      ) : error || verifyError ? (
        <Alert
          message="Error"
          description={
            error ? error : 'An unexpected error occured while attempting to verify your token. Please try again.'
          }
          type="error"
          showIcon
        />
      ) : !init || isVerifying ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Form form={form} onFinish={submitReset} layout="vertical">
          <Title level={3} style={{ color: '#fff', marginBottom: 24 }}>
            {payload.type === 'reset' ? 'Password Reset' : 'Complete Registration'}
          </Title>

          <Form.Item
            label="New Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your new password"
              value={password}
              onChange={handlePasswordChange}
              autoFocus
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[{ required: true, message: 'Please confirm your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              size="large"
            />
          </Form.Item>

          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <Text strong style={{ color: '#fff', fontSize: 16 }}>
              Password Requirements
            </Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
              {[
                { text: 'Cannot match your email address', valid: validation[0] },
                { text: 'Must match your confirmation password', valid: validation[1] },
                { text: 'Must be at least 8 characters long', valid: validation[2] },
                { text: 'Must contain at least 1 numeric character', valid: validation[3] },
                { text: 'Must contain at least 1 special character', valid: validation[4] },
              ].map((req, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {req.valid ? (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
                  )}
                  <Text style={{ color: req.valid ? '#52c41a' : '#fff' }}>{req.text}</Text>
                </div>
              ))}
            </Space>
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
              block
              size="large"
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      )}
    </>
  );
};
